import { useEffect, useState } from 'react'
import { API, embedUrl, fmtMonth, type PortfolioItem } from '../lib/api'

/** Vignette d'un projet : image fournie, sinon vignette YouTube, sinon première image de la vidéo. */
export function Poster({ item }: { item: PortfolioItem }) {
  const yt = item.video_url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/)
  const src = item.thumbnail_url || (yt ? `https://i.ytimg.com/vi/${yt[1]}/hqdefault.jpg` : '')
  if (src) return <img src={src} alt={item.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
  if (item.video_url && !embedUrl(item.video_url))
    return <video src={item.video_url + '#t=0.5'} preload="metadata" muted playsInline className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
  return <div className="absolute inset-0 bg-anthracite" />
}

export function Player({ item, onClose }: { item: PortfolioItem; onClose: () => void }) {
  const embed = embedUrl(item.video_url)
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', k); document.body.style.overflow = 'hidden'
    return () => { window.removeEventListener('keydown', k); document.body.style.overflow = '' }
  }, [onClose])
  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 md:p-10" onClick={onClose} role="dialog" aria-modal="true">
      <button onClick={onClose} aria-label="Fermer" className="absolute top-4 right-5 text-acier hover:text-orange text-3xl leading-none">×</button>
      <div className="w-full max-w-5xl" onClick={e => e.stopPropagation()}>
        <div className="relative aspect-video bg-black border border-white/10">
          {embed
            ? <iframe src={embed} title={item.title} className="absolute inset-0 w-full h-full" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />
            : <video src={item.video_url} poster={item.thumbnail_url || undefined} controls autoPlay playsInline className="absolute inset-0 w-full h-full" />}
        </div>
        <div className="mt-5 flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <h3 className="font-display text-3xl tracking-wide">{item.title}</h3>
          <span className="text-orange text-xs uppercase tracking-[0.3em]">{[item.company, fmtMonth(item.project_date)].filter(Boolean).join(' · ')}</span>
        </div>
        {item.description && <p className="text-acier mt-2 max-w-3xl leading-relaxed whitespace-pre-line">{item.description}</p>}
      </div>
    </div>
  )
}

/** Charge le portfolio public une fois (partagé entre l'accueil et la page /portfolio). */
export function usePortfolio() {
  const [items, setItems] = useState<PortfolioItem[]>([])
  const [loaded, setLoaded] = useState(false)
  useEffect(() => {
    fetch(`${API}/portfolio`).then(r => r.json()).then(j => setItems(j.items || [])).catch(() => {}).finally(() => setLoaded(true))
  }, [])
  return { items, loaded }
}

export function Card({ item, onOpen }: { item: PortfolioItem; onOpen: (i: PortfolioItem) => void }) {
  return (
    <article className="group cursor-pointer" onClick={() => onOpen(item)}>
      <div className="relative aspect-video overflow-hidden bg-anthracite border border-white/10 group-hover:border-orange transition-colors">
        <Poster item={item} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="w-14 h-14 rounded-full bg-orange/90 group-hover:bg-orange flex items-center justify-center shadow-lg transition-transform group-hover:scale-110">
            <svg viewBox="0 0 24 24" className="w-6 h-6 ml-1 fill-blanc"><path d="M8 5v14l11-7z" /></svg>
          </span>
        </div>
        {(item.company || item.project_date) && (
          <span className="absolute left-4 bottom-3 text-[11px] uppercase tracking-[0.3em] text-blanc/90">
            {[item.company, fmtMonth(item.project_date)].filter(Boolean).join(' · ')}
          </span>
        )}
      </div>
      <h3 className="font-display text-2xl tracking-wide mt-4 group-hover:text-orange transition-colors">{item.title}</h3>
      {item.description && <p className="text-acier text-sm leading-relaxed mt-1 line-clamp-3">{item.description}</p>}
    </article>
  )
}

export function PortfolioGrid({ items, limit }: { items: PortfolioItem[]; limit?: number }) {
  const [open, setOpen] = useState<PortfolioItem | null>(null)
  const shown = limit ? items.slice(0, limit) : items
  return (
    <>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {shown.map(item => <Card key={item.id} item={item} onOpen={setOpen} />)}
      </div>
      {open && <Player item={open} onClose={() => setOpen(null)} />}
    </>
  )
}
